/**
 * The welcome galaxy's choreography as pure functions of the simulated time `t` (seconds).
 *
 * Everything on the welcome screen follows one choreography, on one clock
 * (`use-welcome-galaxy-clock.ts`): the headline, the buttons and the launch's sky and splash run on
 * the page's time from a fixed moment after the page is revealed, and the Skia scene joins that
 * time once it can draw without stalling (`welcomeGalaxySceneJoinTime`). Keeping each curve a
 * closed-form function of `t` (rather than integrating state frame by frame) means a fast-forward,
 * a late canvas, or a reduced-motion start at `WELCOME_GALAXY_SETTLED_T` all land on exactly the
 * same picture.
 *
 * The arc is the view from a rocket blasting backwards out of the galaxy. The camera starts inside
 * the core, facing it, and flies straight back along the final line of sight. Characters that were
 * behind the camera stream past it: they appear huge at the screen edges, then shrink away towards
 * the core, while the background stars streak inwards. Meanwhile the whole tilted spiral
 * materialises as the camera draws back.
 * - before 0 s (a cold launch, or a sign-out): the launch out of the splash screen, or out of a
 *   picture of the app, which lives in `welcome-galaxy-launch.ts`; the clock starts at
 *   `-WELCOME_GALAXY_LAUNCH_DURATION`.
 * - 0–2.4 s: the flight, from inside the core to far enough out that the view is near-orthographic.
 *   The ship keeps accelerating the whole way, so the galaxy rushes into frame faster and faster,
 *   and brakes only in the last few tenths of a second, when it snaps into focus.
 * - afterwards: the settled galaxy in a slow perpetual rotation (which the user can also drag).
 *
 * Signing in plays the entry on a second clock, `entryTime` (seconds since the entry began, 0
 * until then): the camera zooms back in without changing the galaxy's orientation. The Learn
 * card becomes a sharp viewport onto it, with heavily blurred overflow as the app appears
 * around it (`welcome-galaxy-entry-host.tsx`, `welcomeGalaxyEntry`).
 */

/** When the camera finishes its flight. */
export const WELCOME_GALAXY_SETTLE_T = 2.4;
/** A moment of the idle phase, used for reduced motion and the static fallback. */
export const WELCOME_GALAXY_SETTLED_T = WELCOME_GALAXY_SETTLE_T + 1.3;
/** Let the buttons finish fading in before UIKit starts morphing the primary glass material. */
export const WELCOME_GALAXY_BUTTON_FROST_START_T = 1.1 * WELCOME_GALAXY_SETTLE_T;
/**
 * By when every curve outside the Skia scene (the launch's splash and sky, the headline, the
 * buttons, including their 800 ms frost transition) has finished, so the views driven by them can
 * stop listening to the clock.
 */
export const WELCOME_GALAXY_INTRO_END_T = WELCOME_GALAXY_BUTTON_FROST_START_T + 0.8;

/** Tilt of the disk away from face-on (68° reads as Andromeda's oblique ellipse). */
export const WELCOME_GALAXY_INCLINATION = (68 * Math.PI) / 180;
/** Rotation of the major axis on screen: lower-left to upper-right. */
export const WELCOME_GALAXY_POSITION_ANGLE = (-32 * Math.PI) / 180;
/** The complete spiral sits in the upper sky, above Hani on the Earth's horizon. */
export const WELCOME_GALAXY_CORE_Y_FRACTION = 0.28;
/** Minimum space between the settled galaxy and each edge of the screen (dp). */
export const WELCOME_GALAXY_EDGE_MARGIN = 20;
/**
 * How far a settled particle can reach past its centre (dp): half the largest glyph's diagonal,
 * since glyphs rest slightly tilted.
 */
const PARTICLE_OVERHANG = 12;
/** Caps the disk radius on wide viewports, preserving room for the title and account actions. */
const RADIUS_MAX_HEIGHT_FRACTION = 0.42;
/**
 * Half-extents of the settled r = 1 disk on screen, in disk radii: the bounding box of the tilted
 * (squashed by cos inclination), then rotated, unit ellipse. The spin never changes them, since it
 * rotates the disk within its own ellipse.
 */
const HALF_WIDTH = Math.hypot(
  Math.cos(WELCOME_GALAXY_POSITION_ANGLE),
  Math.sin(WELCOME_GALAXY_POSITION_ANGLE) * Math.cos(WELCOME_GALAXY_INCLINATION),
);
const HALF_HEIGHT = Math.hypot(
  Math.sin(WELCOME_GALAXY_POSITION_ANGLE),
  Math.cos(WELCOME_GALAXY_POSITION_ANGLE) * Math.cos(WELCOME_GALAXY_INCLINATION),
);

/**
 * The camera's distance from the core along the line of sight, in disk radii: it starts inside the
 * bulge and ends far enough out that perspective has all but vanished.
 */
export const WELCOME_GALAXY_FLIGHT_START_DISTANCE = 0.1;
export const WELCOME_GALAXY_FLIGHT_END_DISTANCE = 10;
/** The disk's slow, steady rotation (rad/s). */
const SPIN_RATE = 0.05;

/**
 * The flight's speed profile, in log-distance (which is what the eye follows: the galaxy's
 * apparent size changes by the same factor per e-fold however far out the camera is).
 *
 * The ship is already rolling at launch (`FLIGHT_START_SPEED`, relative to the acceleration
 * term) and its speed grows as `x^(p - 1)` on top of that, so it is moving fastest just before
 * the brake. The brake is a constant deceleration over the last `FLIGHT_BRAKE_FRACTION` of the
 * flight, from full speed to a dead stop: the galaxy rushes up to its settled size and locks
 * into focus, rather than drifting the last of the way in.
 */
const FLIGHT_START_SPEED = 0.45;
const FLIGHT_POWER = 2.5;
const FLIGHT_BRAKE_FRACTION = 0.16;
const FLIGHT_BRAKE_START = 1 - FLIGHT_BRAKE_FRACTION;
/** Log-distance progress (unnormalised) and speed at the moment the brake starts. */
const FLIGHT_BRAKE_START_PROGRESS =
  FLIGHT_START_SPEED * FLIGHT_BRAKE_START + FLIGHT_BRAKE_START ** FLIGHT_POWER;
const FLIGHT_BRAKE_START_SPEED =
  FLIGHT_START_SPEED + FLIGHT_POWER * FLIGHT_BRAKE_START ** (FLIGHT_POWER - 1);
/** Where the braking ship comes to rest (unnormalised), which maps to 1. */
const FLIGHT_TOTAL_PROGRESS =
  FLIGHT_BRAKE_START_PROGRESS + (FLIGHT_BRAKE_START_SPEED * FLIGHT_BRAKE_FRACTION) / 2;

export type WelcomeGalaxyViewport = {
  /** Hero enlargement: the outer character arms can reach the stage's edges. Defaults to 1. */
  galaxyScale?: number;
  height: number;
  /**
   * The width, from the left edge, that the settled galaxy and its wordmark are centred in. The
   * canvas still fills the whole viewport; landscape gives the right half to the account panel.
   * Defaults to `width`.
   */
  stageWidth?: number;
  width: number;
};

export function clamp01(value: number): number {
  "worklet";
  return value < 0 ? 0 : Math.min(value, 1);
}

export function lerp(from: number, to: number, progress: number): number {
  "worklet";
  return from + (to - from) * progress;
}

/** Ken Perlin's smootherstep between `edge0` and `edge1`, clamped. */
export function smootherstep(edge0: number, edge1: number, value: number): number {
  "worklet";
  const x = clamp01((value - edge0) / (edge1 - edge0));
  return x * x * x * (x * (x * 6 - 15) + 10);
}

/**
 * `smootherstep` between two moments given as fractions of the flight, so every fade stays in
 * step with the flight however long it is tuned to last.
 */
export function flightSmootherstep(fromFraction: number, toFraction: number, t: number): number {
  "worklet";
  return smootherstep(
    fromFraction * WELCOME_GALAXY_SETTLE_T,
    toFraction * WELCOME_GALAXY_SETTLE_T,
    t,
  );
}

/**
 * How far along the flight the camera is (0 → 1), in log-distance: accelerating from a rolling
 * start, then braking hard over the last `FLIGHT_BRAKE_FRACTION` of it.
 */
export function welcomeGalaxyFlight(t: number): number {
  "worklet";
  const x = clamp01(t / WELCOME_GALAXY_SETTLE_T);
  if (x <= FLIGHT_BRAKE_START) {
    return (FLIGHT_START_SPEED * x + x ** FLIGHT_POWER) / FLIGHT_TOTAL_PROGRESS;
  }
  const braking = x - FLIGHT_BRAKE_START;
  return (
    (FLIGHT_BRAKE_START_PROGRESS +
      FLIGHT_BRAKE_START_SPEED * braking -
      (FLIGHT_BRAKE_START_SPEED * braking * braking) / (2 * FLIGHT_BRAKE_FRACTION)) /
    FLIGHT_TOTAL_PROGRESS
  );
}

/** When the brake starts, and the galaxy begins to lock into focus. */
export const WELCOME_GALAXY_FLIGHT_BRAKE_T = FLIGHT_BRAKE_START * WELCOME_GALAXY_SETTLE_T;

/**
 * `smootherstep` between two points of the flight given as fractions of its log-distance (0 at
 * the core, 1 settled): what resolves as the camera draws back resolves with its motion, so it
 * rushes in with the ship and locks in as it brakes, whatever the speed profile.
 */
export function focusSmootherstep(fromProgress: number, toProgress: number, t: number): number {
  "worklet";
  return smootherstep(fromProgress, toProgress, welcomeGalaxyFlight(t));
}

/** The camera's distance from the core (disk radii), growing exponentially over the flight. */
export function welcomeGalaxyCameraDistance(t: number): number {
  "worklet";
  const start = WELCOME_GALAXY_FLIGHT_START_DISTANCE;
  const end = WELCOME_GALAXY_FLIGHT_END_DISTANCE;
  return start * (end / start) ** welcomeGalaxyFlight(t);
}

/** How long the entry into the Haniverse lasts once someone signs in (s). */
export const WELCOME_GALAXY_ENTRY_DURATION = 2;
/**
 * When the entry host's canvas takes over from the welcome page's and the app is navigated to
 * underneath it: once the headline and buttons have faded out, so the two canvases match.
 */
export const WELCOME_GALAXY_ENTRY_HANDOFF_T = 0.35;
/**
 * Only one canvas needs to draw the entry at a time: the entry host's starts this long before it
 * takes over (so its first frame is ready), and the welcome page's stops this long after.
 */
export const WELCOME_GALAXY_ENTRY_HANDOFF_MARGIN = 0.1;
/**
 * How far the camera zooms back in on entry when nothing says otherwise.
 */
export const WELCOME_GALAXY_ENTRY_ZOOM = 1.6;
/**
 * How far the camera dives on entry when the galaxy becomes no card but the onboarding's Earth: on
 * through the arms and into the core, whose glow the Earth then comes out of.
 */
export const WELCOME_GALAXY_DIVE_ZOOM = 7;
/**
 * The dive dissolves to show the onboarding's Earth already coming out of the core beneath it,
 * while the camera is still diving: from once the Earth is on screen (`diveRevealStart`), but no
 * sooner than this (s), just after the app has been navigated to underneath it…
 */
export const WELCOME_GALAXY_DIVE_REVEAL_EARLIEST = 0.5;
/** …and no later than this (s), Earth or no Earth, so the dive can never hang over the app… */
export const WELCOME_GALAXY_DIVE_REVEAL_DEADLINE = 1.6;
/** …over this long (s). */
export const WELCOME_GALAXY_DIVE_REVEAL_DURATION = 1.1;
/** The longest a dive can last: revealed at its deadline. */
export const WELCOME_GALAXY_DIVE_MAX_DURATION = Math.max(
  WELCOME_GALAXY_ENTRY_DURATION,
  WELCOME_GALAXY_DIVE_REVEAL_DEADLINE + WELCOME_GALAXY_DIVE_REVEAL_DURATION,
);
const ENTRY_ZOOM_MIN = 1;
const ENTRY_ZOOM_MAX = 2.5;
/**
 * The body's radius (disk radii): a little past r = 1, so it covers every disk sprite's resting
 * overhang once the galaxy has to stand on its own over the app.
 */
export const WELCOME_GALAXY_ENTRY_BODY_RADIUS = 1.1;

/**
 * The Learn card's viewport in window coordinates (dp), including its corner radius.
 */
export type WelcomeGalaxyEntryCard = {
  height: number;
  radius: number;
  width: number;
  x: number;
  y: number;
};

/** The card's corner radius (`rounded-xs`), should the card not say. */
export const WELCOME_GALAXY_ENTRY_CARD_RADIUS = 2;

/**
 * How far along the zoom back in the camera is (0 → 1): it eases off the settled view, gathers
 * speed and settles behind the card's viewport.
 */
export function welcomeGalaxyEntryDive(entryTime: number): number {
  "worklet";
  return smootherstep(
    0.08 * WELCOME_GALAXY_ENTRY_DURATION,
    0.72 * WELCOME_GALAXY_ENTRY_DURATION,
    entryTime,
  );
}

/** The headline and buttons fade out as the entry begins, before the entry host takes over. */
export function welcomeGalaxyEntryUiOpacity(entryTime: number): number {
  "worklet";
  return 1 - smootherstep(0, 0.9 * WELCOME_GALAXY_ENTRY_HANDOFF_T, entryTime);
}

/**
 * The entry host's whole canvas over the app: invisible until it takes over, whole through the
 * zoom, and gone as the galaxy gives way to the card's content. A dive (`diveRevealStart` given)
 * instead stays whole until its reveal starts (`null` until then), then dissolves while it is
 * still diving, so the Earth beneath seems to come out of the galaxy.
 */
export function welcomeGalaxyEntryOverlayOpacity(
  entryTime: number,
  diveRevealStart?: number | null,
): number {
  "worklet";
  if (entryTime < WELCOME_GALAXY_ENTRY_HANDOFF_T) {
    return 0;
  }
  if (diveRevealStart === null) {
    return 1;
  }
  return diveRevealStart === undefined
    ? 1 -
        smootherstep(0.82 * WELCOME_GALAXY_ENTRY_DURATION, WELCOME_GALAXY_ENTRY_DURATION, entryTime)
    : 1 -
        smootherstep(
          diveRevealStart,
          diveRevealStart + WELCOME_GALAXY_DIVE_REVEAL_DURATION,
          entryTime,
        );
}

/**
 * When a dive starts to dissolve (s), as its clock reaches `entryTime`: once the Earth it reveals
 * is on screen beneath it (`isTargetShown`) and no sooner than `WELCOME_GALAXY_DIVE_REVEAL_EARLIEST`,
 * or at `WELCOME_GALAXY_DIVE_REVEAL_DEADLINE` regardless; `null` while it waits.
 */
export function computeWelcomeGalaxyDiveRevealStart(
  entryTime: number,
  isTargetShown: boolean,
): number | null {
  "worklet";
  if (entryTime >= WELCOME_GALAXY_DIVE_REVEAL_DEADLINE) {
    return WELCOME_GALAXY_DIVE_REVEAL_DEADLINE;
  }
  return isTargetShown ? Math.max(entryTime, WELCOME_GALAXY_DIVE_REVEAL_EARLIEST) : null;
}

/**
 * The viewport and opacity choreography on entry. The galaxy keeps its characters and its
 * orientation; only its overflow is blurred.
 */
export type WelcomeGalaxyEntryState = {
  /** The opaque card surface under the galaxy. */
  bodyOpacity: number;
  /** The card's 1pt border, drawn as the morph completes. */
  borderOpacity: number;
  /** The dust lanes remain visible during the zoom. */
  dustOpacity: number;
  /** Blur sigma in screen points, applied only outside the sharp viewport. */
  exteriorBlur: number;
  exteriorOpacity: number;
  /** The halo and core glow dim before the card content appears. */
  glowOpacity: number;
  /** 0 for the full screen, 1 for the measured card viewport. */
  morph: number;
  /** The sky behind everything, which fades out over the app so its chrome fades in. */
  skyOpacity: number;
  /** Entry rotation stays zero: the camera only zooms. */
  spin: number;
  spinSpeed: number;
  /** Spin streaks stay hidden so the original characters remain readable. */
  spinStreakOpacity: number;
  /** Disk sprites retain their opacity until the whole overlay fades. */
  spriteOpacity: number;
  /** How far the body's colour has gone from the sky's to the card's surface. */
  surfaceMix: number;
  /** Retains the welcome galaxy's original tilt (1). */
  tilt: number;
};

export function welcomeGalaxyEntry(entryTime: number): WelcomeGalaxyEntryState {
  "worklet";
  const u = clamp01(entryTime / WELCOME_GALAXY_ENTRY_DURATION);
  const morph = smootherstep(
    WELCOME_GALAXY_ENTRY_HANDOFF_T,
    0.45 * WELCOME_GALAXY_ENTRY_DURATION,
    entryTime,
  );
  return {
    // In place before the sky starts to go, so the galaxy never shows the app through itself.
    bodyOpacity: entryTime <= 0 ? 0 : smootherstep(0.08, 0.16, u),
    borderOpacity: smootherstep(0.4, 0.55, u),
    dustOpacity: 1,
    exteriorBlur: 32 * smootherstep(0.08, 0.4, u),
    exteriorOpacity: 1 - 0.75 * morph,
    glowOpacity: 1 - smootherstep(0.7, 0.9, u),
    morph,
    skyOpacity: 1 - smootherstep(0.2, 0.45, u),
    spin: 0,
    spinSpeed: 0,
    spinStreakOpacity: 0,
    spriteOpacity: 1,
    surfaceMix: smootherstep(0.78, 0.95, u),
    tilt: 1,
  };
}

/**
 * The camera's distance from the core (disk radii), including the zoom back in once someone has
 * signed in (`entryTime` > 0), to `entryZoom` times closer than settled.
 */
export function welcomeGalaxyViewDistance(
  t: number,
  entryTime: number,
  entryZoom = WELCOME_GALAXY_ENTRY_ZOOM,
): number {
  "worklet";
  return welcomeGalaxyCameraDistance(t) * entryZoom ** -welcomeGalaxyEntryDive(entryTime);
}

/** Magnification of the core's depth relative to the settled view (1 once settled). */
export function welcomeGalaxyZoom(
  t: number,
  entryTime = 0,
  entryZoom = WELCOME_GALAXY_ENTRY_ZOOM,
): number {
  "worklet";
  return WELCOME_GALAXY_FLIGHT_END_DISTANCE / welcomeGalaxyViewDistance(t, entryTime, entryZoom);
}

/**
 * How much of the galaxy's diffuse halo has resolved: it is drawn flat on the disk plane, so it
 * comes in as the view loses its perspective, rushing in with the ship and locking in as it
 * brakes.
 */
export function welcomeGalaxyMaterialize(t: number): number {
  "worklet";
  return focusSmootherstep(0.55, 0.97, t);
}

/**
 * Where the Skia scene's clock joins the page's (`useWelcomeGalaxyClock`) once the scene can draw
 * without stalling, at the page's time `t`. Until then a still stand-in is drawn in its place
 * (`WelcomeGalaxyStandIn`), which resolves its galaxy as the flight's halo would
 * (`welcomeGalaxyMaterialize`). Before that, the stand-in has shown only the launch's sky, so the
 * scene joins in step and plays the rest of the launch and the flight. After it, the stand-in's
 * galaxy is the settled one, so the scene joins settled: it never replays a flight the page has
 * already moved past, and the crossfade from the stand-in finds the very same galaxy.
 *
 * Defined after `welcomeGalaxyMaterialize`, which it calls (see `welcomeGalaxyEntryZoomFor`).
 */
export function welcomeGalaxySceneJoinTime(t: number): number {
  "worklet";
  return welcomeGalaxyMaterialize(t) > 0 ? WELCOME_GALAXY_SETTLED_T : t;
}

export function welcomeGalaxySpin(t: number): number {
  "worklet";
  return SPIN_RATE * t;
}

/**
 * The tagline and buttons are on screen from the page's very first frame, mostly opaque and
 * tappable, so nobody ever looks at a page without them or waits for the intro: on a cold launch
 * they sit over the copy of the splash from before the clock starts. They come up to full strength
 * as the galaxy settles.
 */
export function welcomeGalaxyButtonsOpacity(t: number): number {
  "worklet";
  return 0.6 + 0.4 * flightSmootherstep(0.62, 1.08, t);
}

/** When the Haniverse wordmark starts to appear: as the ship brakes and the galaxy locks in. */
export const WELCOME_GALAXY_HEADLINE_START_T = WELCOME_GALAXY_FLIGHT_BRAKE_T;
/** When the wordmark is whole, a beat after the galaxy has settled. */
export const WELCOME_GALAXY_HEADLINE_END_T = WELCOME_GALAXY_SETTLE_T + 0.2;

/** The Haniverse wordmark, over the galaxy. */
export function welcomeGalaxyHeadlineProgress(t: number): number {
  "worklet";
  return smootherstep(WELCOME_GALAXY_HEADLINE_START_T, WELCOME_GALAXY_HEADLINE_END_T, t);
}

/** Whether the welcome page splits into the galaxy on the left and the account panel on the right. */
export function isWelcomeGalaxySplitLayout(viewport: WelcomeGalaxyViewport): boolean {
  return viewport.width > viewport.height;
}

/**
 * The settled galaxy's core and disk radius (r = 1) on screen. The radius is the largest that keeps
 * the whole settled disk, glyphs included, `WELCOME_GALAXY_EDGE_MARGIN` inside its stage.
 */
export function welcomeGalaxyPlacement(viewport: WelcomeGalaxyViewport) {
  "worklet";
  const stageWidth = viewport.stageWidth ?? viewport.width;
  const centerY = viewport.height * WELCOME_GALAXY_CORE_Y_FRACTION;
  const inset = WELCOME_GALAXY_EDGE_MARGIN + PARTICLE_OVERHANG;
  return {
    centerX: stageWidth / 2,
    centerY,
    radius:
      (viewport.galaxyScale ?? 1) *
      Math.max(
        0,
        Math.min(
          (stageWidth / 2 - inset) / HALF_WIDTH,
          (centerY - inset) / HALF_HEIGHT,
          viewport.height * RADIUS_MAX_HEIGHT_FRACTION,
        ),
      ),
  };
}

/**
 * Where the Learn card is expected until it reports its own frame: the card stage's 9pt gutters,
 * under the toolbar and above the action row, as a fraction of a phone screen.
 */
export function welcomeGalaxyEntryCardFallback(
  viewport: WelcomeGalaxyViewport,
): WelcomeGalaxyEntryCard {
  "worklet";
  const inset = 9;
  const top = 0.14 * viewport.height;
  const bottom = 0.86 * viewport.height;
  return {
    height: bottom - top,
    radius: WELCOME_GALAXY_ENTRY_CARD_RADIUS,
    width: Math.max(0, viewport.width - 2 * inset),
    x: inset,
    y: top,
  };
}

/**
 * The zoom the entry dives to, covering the card's viewport with the oblique disk while
 * preserving its inclination and position angle.
 *
 * Defined after `welcomeGalaxyPlacement`, which it calls: the worklets plugin captures a
 * worklet's closure when its definition is evaluated, so a worklet that calls one defined later
 * in the module would carry `undefined` for it on the UI thread.
 */
export function welcomeGalaxyEntryZoomFor(
  card: WelcomeGalaxyEntryCard,
  viewport: WelcomeGalaxyViewport,
): number {
  "worklet";
  const { radius } = welcomeGalaxyPlacement(viewport);
  if (radius <= 0) {
    return WELCOME_GALAXY_ENTRY_ZOOM;
  }
  const diameter = 2 * WELCOME_GALAXY_ENTRY_BODY_RADIUS * radius;
  const cover = Math.max(
    card.width / (diameter * HALF_WIDTH),
    card.height / (diameter * HALF_HEIGHT),
  );
  return Math.max(ENTRY_ZOOM_MIN, Math.min(ENTRY_ZOOM_MAX, cover));
}

/** Compact native text box: enough room for CJK roofs, directly above the tagline. */
export const WELCOME_GALAXY_HEADLINE_ASPECT = 0.28;

/**
 * The wordmark spans its stage, short of the landscape notch, centred below Hani.
 */
export function welcomeGalaxyHeadlineWidth(viewport: WelcomeGalaxyViewport, sideInset = 0): number {
  return Math.max(0, (viewport.stageWidth ?? viewport.width) - 2 * sideInset);
}

export function welcomeGalaxyHeadlineHeight(
  viewport: WelcomeGalaxyViewport,
  sideInset = 0,
): number {
  return welcomeGalaxyHeadlineWidth(viewport, sideInset) * WELCOME_GALAXY_HEADLINE_ASPECT;
}

export type WelcomeGalaxyCamera = {
  /** Screen position of the galaxy core (the vanishing point of the flight). */
  centerX: number;
  centerY: number;
  cosInclination: number;
  /** Distance from the core along the line of sight (disk radii). */
  distance: number;
  /** The disk's tilt away from face-on; it turns to face the viewer on entry. */
  inclination: number;
  /**
   * Perspective strength: a point `depth` disk radii towards the camera is magnified by
   * `1 / (1 - perspective * depth)`, and is behind the camera once that denominator reaches 0.
   */
  perspective: number;
  positionAngle: number;
  /** Screen size of r = 1 at the core's depth: the settled disk radius times the zoom. */
  scale: number;
  sinInclination: number;
  /** Magnification of the core's depth relative to the settled view (1 once settled). */
  zoom: number;
};

/**
 * A pinhole camera flying back along the line of sight, with its focal length fixed so that the
 * end of the flight frames the settled galaxy. The perspective is offset by its end value so the
 * settled view is exactly orthographic (and matches the flat disk-plane layers). On entry
 * (`entryTime` > 0) it zooms back in to `entryZoom`, keeping the disk's orientation.
 */
export function computeWelcomeGalaxyCamera(
  t: number,
  viewport: WelcomeGalaxyViewport,
  entryTime = 0,
  entryZoom = WELCOME_GALAXY_ENTRY_ZOOM,
): WelcomeGalaxyCamera {
  "worklet";
  const distance = welcomeGalaxyViewDistance(t, entryTime, entryZoom);
  const zoom = WELCOME_GALAXY_FLIGHT_END_DISTANCE / distance;
  const { centerX, centerY, radius } = welcomeGalaxyPlacement(viewport);
  const tilt = entryTime > 0 ? welcomeGalaxyEntry(entryTime).tilt : 1;
  const inclination = WELCOME_GALAXY_INCLINATION * tilt;
  return {
    centerX,
    centerY,
    cosInclination: Math.cos(inclination),
    distance,
    inclination,
    perspective: 1 / distance - 1 / WELCOME_GALAXY_FLIGHT_END_DISTANCE,
    // Face-on is exactly 0, not the -0 of a negative angle times 0.
    positionAngle: tilt > 0 ? WELCOME_GALAXY_POSITION_ANGLE * tilt : 0,
    scale: radius * zoom,
    sinInclination: Math.sin(inclination),
    zoom,
  };
}

/**
 * The disk plane's screen transform for zero-depth content (the glow, dust lanes and core), as a
 * Skia/RN transform list. It matches the particle projection exactly once perspective has faded.
 */
export function welcomeGalaxyDiskTransform(camera: WelcomeGalaxyCamera) {
  "worklet";
  return [
    { translateX: camera.centerX },
    { translateY: camera.centerY },
    { rotate: camera.positionAngle },
    { scaleY: camera.cosInclination },
    { scale: camera.scale },
  ];
}
